import type { GlobalSearchResults } from '@/api/shell';
import i18n from '@/i18n';
import { initialsOf } from './shellFormat';

/**
 * EAA-1813: flattens `/api/v1/search`'s five-key grouped payload into the row
 * model frame 13 renders.
 *
 * Two things this deliberately does NOT do:
 *
 * 1. It never derives a `type`/`label`/`url` field — only the group labels and
 *    the tile initials are computed here. Result `target`s come from the API,
 *    but see the route-remap block below: three of the five are NOT routes this
 *    SPA mounts, so they are remapped rather than trusted.
 * 2. It never adds a group the API does not return. Frame 13 shows a third
 *    "HÀNH ĐỘNG" group with a contextual command ("Cấp tài khoản trong Tân An");
 *    the search endpoint returns entities only, with no action/command source
 *    anywhere behind it, so that group is absent rather than faked.
 */

export type PaletteGroupKey = keyof GlobalSearchResults;

export interface PaletteRow {
    /** Group-prefixed: ids are only unique within a group, not across them. */
    key: string;
    title: string;
    caption?: string;
    /** Right-aligned monospace metadata — a code or a role key. */
    meta?: string;
    /** A route this SPA actually mounts — see the route-remap block below. */
    target: string;
    initials: string;
}

export interface PaletteGroup {
    key: PaletteGroupKey;
    label: string;
    rows: PaletteRow[];
    /**
     * Index of this group's first row within the flat, cross-group active
     * index. Arrow-key navigation runs over ALL results as one list (frame 13's
     * keyboard model), while the DOM stays grouped — this is what reconciles
     * the two without a second flat render pass.
     */
    offset: number;
}

/**
 * EAA-1813: group headers use the portal's settled glossary — "Cơ sở" for a
 * tenant (the word "Tenant" is not used in visible copy anywhere in the
 * console), and "Level" left in English, matching ConsoleNav and the hierarchy
 * screens. "Cấp" is reserved for hierarchy depth and for "Cấp quyền", so it is
 * deliberately not used for the Level entity here.
 *
 * EAA-1845: this is a plain module (no component, no hook), so it reads
 * through the shared `i18n` instance directly rather than `useTranslation()`.
 * Computed on each call (not a module-scope constant) so a future language
 * switch is picked up immediately.
 */
function groupLabels(): Record<PaletteGroupKey, string> {
    return {
        organizations: i18n.t('shell.commandPalette.groupOrganizations'),
        tenants: i18n.t('shell.commandPalette.groupTenants'),
        applications: i18n.t('shell.commandPalette.groupApplications'),
        levels: i18n.t('shell.commandPalette.groupLevels'),
        accounts: i18n.t('shell.commandPalette.groupAccounts'),
    };
}

/**
 * EAA-1813: the search API's `target` is FORWARD-LOOKING for three of its five
 * groups — it names routes `App.tsx` does not mount. Checked against the router
 * one by one:
 *
 *   organizations  /organizations/{id}                            EXISTS
 *   applications   /console/apps                                  EXISTS
 *   tenants        /organizations/{orgId}/tenants/{scopeId}        NO ROUTE
 *   levels         /organizations/{orgId}/levels/{levelId}         NO ROUTE
 *   accounts       /organizations/{orgId}/assignments?user_sub=…   NO ROUTE
 *
 * There is no catch-all `*` route, so react-router matches NOTHING for those
 * three and `<Routes>` renders null — the operator gets a blank page, which is
 * strictly worse than landing one level up on a screen that does show the
 * record. Each remap below is therefore to the nearest real, verified route:
 *
 *   - tenants  -> the org detail page's "Cơ sở" tab. Its tab state is URL-backed
 *                 (`?tab=tenants`, see OrganizationDetailPage), so this is a
 *                 genuine deep link, just one level short of the tenant itself.
 *   - levels   -> /console/hierarchy, the screen that manages Levels. It reads
 *                 no URL params, so the org cannot be pre-selected.
 *   - accounts -> /console/accounts. AccountsPage keeps its filters in local
 *                 state (no `?username=`), so the sub cannot be pre-filtered
 *                 either — the same limitation AccountDetailModal already
 *                 documents for its "Xem nhật ký" link.
 *
 * Delete a remap the day its real route ships; do NOT instead add the deep-link
 * routes here, and do not pass the raw target through "because the API said so".
 */
const LEVELS_TARGET = '/console/hierarchy';
const ACCOUNTS_TARGET = '/console/accounts';

export function buildPaletteGroups(results: GlobalSearchResults | undefined): PaletteGroup[] {
    if (!results) return [];

    const GROUP_LABELS = groupLabels();
    const byGroup: Record<PaletteGroupKey, PaletteRow[]> = {
        organizations: results.organizations.map((org) => ({
            key: `organizations:${org.id}`,
            title: org.name,
            meta: org.code,
            target: org.target,
            initials: initialsOf(org.name),
        })),
        tenants: results.tenants.map((tenant) => ({
            key: `tenants:${tenant.id}`,
            title: tenant.name,
            caption: `${tenant.organization_code} · ${tenant.application_code}`,
            target: `/organizations/${tenant.organization_id}?tab=tenants`,
            initials: initialsOf(tenant.name),
        })),
        applications: results.applications.map((app) => ({
            key: `applications:${app.id}`,
            title: app.name,
            // `enabled` is a real serialized field, so a disabled integration
            // can say so instead of looking identical to an active one.
            caption: app.enabled ? undefined : i18n.t('shell.commandPalette.appDisabledCaption'),
            meta: app.code,
            target: app.target,
            initials: initialsOf(app.name),
        })),
        levels: results.levels.map((level) => ({
            key: `levels:${level.id}`,
            title: level.name,
            caption: level.organization_code,
            meta: level.role_key,
            target: LEVELS_TARGET,
            initials: initialsOf(level.name),
        })),
        // An account result carries `user_email` + `organization_id` and nothing
        // else — no display name, and no organization NAME (only its uuid). Frame
        // 13 shows "gv.tan.an — Trường Tiểu học Tân An"; rendering the uuid in
        // that slot would be worse than leaving it empty, so the row is the email
        // alone. A caption appears here only once the endpoint serializes the
        // organization's name or code.
        accounts: results.accounts.map((account) => ({
            key: `accounts:${account.user_sub}`,
            title: account.user_email,
            target: ACCOUNTS_TARGET,
            initials: initialsOf(account.user_email),
        })),
    };

    const order: PaletteGroupKey[] = [
        'organizations',
        'tenants',
        'applications',
        'levels',
        'accounts',
    ];

    const groups: PaletteGroup[] = [];
    let offset = 0;
    for (const key of order) {
        const rows = byGroup[key];
        if (rows.length === 0) continue;
        groups.push({ key, label: GROUP_LABELS[key], rows, offset });
        offset += rows.length;
    }
    return groups;
}
